import 'dayjs/locale/sv';
import 'react-photo-view/dist/react-photo-view.css';

import React, { useState } from 'react';

import { Trans, useTranslation } from 'react-i18next';
import { usePublicationReport } from '@/hooks/influencer';

import dayjs from 'dayjs';

import { Check, Clock, ExternalLink, Download } from 'react-feather';
import { MediaItem } from '@/components/media-item';
import { Button, Link } from '@/components';
import { TextInput } from '@/components/text-input';
import { PublicationReportResponse } from '@/hooks/influencer/usePublicationReport';
import { useQueryClient } from '@tanstack/react-query';
import { getMediaThumbnailUrl, getVideoPosterUrl } from '@/utils/mediaUtils';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import type { OverlayRenderProps } from 'react-photo-view/dist/types';
import styles from './CollaborationItem.module.css';

function getFileType(url: string) {
  const fileExtension = (url.split('.') as TypeAny).pop().toLowerCase();
  const videoExtensions = ['mp4', 'avi', 'mov', 'webm'];

  if (videoExtensions.includes(fileExtension)) return 'video';

  return 'img';
}

function slideshowOverlay(overlay: OverlayRenderProps, t: TypeAny) {
  if (!overlay.images[overlay.index].src) return null;

  const overlayFile = overlay.images[overlay.index].src as string;

  const overlayFileType = getFileType(overlayFile);

  return (
    <>
      {overlayFileType === 'video' && (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video
          key={`publication-overlay-${overlay.index}`}
          style={{
            background: 'var(--color-black)',
            display: 'block',
            height: '100%',
            position: 'absolute',
            width: '100%',
            zIndex: 10
          }}
          controls
          playsInline
          poster={getVideoPosterUrl(overlayFile)}
        >
          <source src={overlayFile} type="video/mp4" />
        </video>
      )}

      <div
        style={{
          bottom: '4.5rem',
          padding: '0.5rem',
          position: 'fixed',
          right: 0,
          zIndex: 11
        }}
      >
        <Link href={overlayFile} theme="cta" target="_blank">
          <span
            style={{
              alignItems: 'center',
              display: 'flex',
              gap: '0.5rem'
            }}
          >
            {t('carousel.download')}
            <Download size={16} />
          </span>
        </Link>
      </div>
    </>
  );
}

interface PublicationProps {
  publication: Api.Influencer.Publication;
  setStatus: (code: string) => void;
}

export function Publication({ publication, setStatus }: PublicationProps) {
  const [isLinkEditing, setIsLinkEditing] = useState(false);
  const [isSparkIdEditing, setIsSparkIdEditing] = useState(false);
  const [form, setForm] = useState(() => ({
    permalink: publication.permalink || '',
    sparkId: publication.spark_id || ''
  }));

  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutation, isLoading: isReporting } = usePublicationReport({
    permalink: form.permalink,
    sparkId: form.sparkId || '',
    id: publication.publication_id,
    onSuccess: (data: PublicationReportResponse | ResponseObject) => {
      if ('publication' in data) {
        const key = `influencer-collaboration-${data.publication.collaboration_id}-${data.publication.beat_id}-publications`;

        queryClient.setQueryData([key, null], (oldItems: TypeAny) => {
          if (oldItems?.publications) {
            const res = oldItems.publications.map((item: Api.Influencer.Publication) =>
              item.publication_id === data.publication.publication_id ? data.publication : item
            );

            return { publications: res };
          }
          return { publications: [] };
        });
      }
      if (['BEAT_FINISHED', 'COLLABORATION_FINISHED'].includes(data.code)) {
        setStatus(data.code);
      }
      setIsLinkEditing(false);
      setIsSparkIdEditing(false);
    }
  });

  function handleChangeForm(val: string, key: string) {
    setForm({
      ...form,
      [key]: val
    });
  }
  function handleSubmit() {
    if (isReporting) return;
    mutation();
  }

  return (
    <div className={`${styles.draftWrap} ${styles.expand}`}>
      <div className={`${styles.header} ${styles.publicationHeader}`}>
        <div className={styles.headerName}>{publication.product_name}</div>
        <div className={styles.headerDate}>
          {t('campaignPageDrafts.headingDue')}:
          <span> {dayjs(publication.date_publish).format('DD.MM.YYYY')}</span>
        </div>
      </div>
      <div className={styles.body}>
        <div className={styles.content}>
          <div className={styles.data}>
            {!!publication.approved_draft?.medias.length && (
              <div className={styles.dataRow}>
                <div className={styles.rowInfo}>
                  <div className={styles.infoTitle}>{t('publication.approvedMaterials')}:</div>
                </div>
                <div className={styles.rowContent}>
                  <PhotoProvider overlayRender={(overlay) => slideshowOverlay(overlay, t)}>
                    {publication.approved_draft?.medias
                      .filter((m) => !m.is_raw)
                      .map((m) => (
                        <PhotoView key={m.url} src={m.url}>
                          <MediaItem key={m.url} src={getMediaThumbnailUrl(m)} />
                        </PhotoView>
                      ))}
                  </PhotoProvider>
                </div>
              </div>
            )}
            {publication.approved_draft?.caption && (
              <div className={styles.dataRow}>
                <div className={styles.rowInfo}>
                  <div className={styles.infoTitle}>{t('publication.caption')}</div>
                </div>
                <div className={styles.rowContent}>{publication.approved_draft?.caption}</div>
              </div>
            )}
          </div>
        </div>
        <div className={`${styles.footer} ${styles.publicationFooter}`}>
          <div className={styles.inputsWrap}>
            <div className={`${styles.flex} ${styles.inputWrap}`}>
              {(publication.required.includes('PERMALINK') ||
                publication.optional.includes('PERMALINK')) &&
                (publication.status === 'PLANNED' || isLinkEditing ? (
                  <>
                    <span>{t('publication.postLink')}</span>
                    <TextInput
                      value={form.permalink}
                      noSpacing
                      placeholder={t('publication.linkPlaceholder')}
                      onChange={(e) => handleChangeForm(e.target.value, 'permalink')}
                    />
                  </>
                ) : (
                  <>
                    <a
                      href={publication.permalink}
                      target="_blank"
                      className={styles.publicationLink}
                    >
                      {t('publication.viewPost')}
                      <ExternalLink size={16} />
                    </a>
                    <span className={styles.editButton} onClick={() => setIsLinkEditing(true)}>
                      {t('actions.edit')}
                    </span>
                  </>
                ))}
            </div>
            <div className={`${styles.flex} ${styles.inputWrap}`}>
              {(publication.required.includes('SPARK_ID') ||
                publication.optional.includes('SPARK_ID')) &&
                (publication.status === 'PLANNED' || isSparkIdEditing ? (
                  <>
                    <span>{t('publication.sparkID')}</span>
                    <TextInput
                      value={form.sparkId}
                      noSpacing
                      placeholder={t('publication.sparkPlaceholder')}
                      onChange={(e) => handleChangeForm(e.target.value, 'sparkId')}
                    />
                  </>
                ) : (
                  <>
                    <span>{`${t('publication.sparkID')}: ${publication.spark_id}`}</span>
                    <span className={styles.editButton} onClick={() => setIsSparkIdEditing(true)}>
                      {t('actions.edit')}
                    </span>
                  </>
                ))}
            </div>
          </div>
          {(isLinkEditing || isSparkIdEditing) && (
            <span className={styles.editButton} onClick={handleSubmit}>
              {t('actions.save')}
            </span>
          )}
          {publication.status === 'PUBLISHED' ? (
            <Button disabled isLoading={isReporting} className={styles.publishedButton}>
              {t('translatedType.PUBLISHED')}
              <Check size={18} />
            </Button>
          ) : (
            <Button
              disabled={
                (publication.required.includes('PERMALINK') && !form.permalink) ||
                (publication.required.includes('SPARK_ID') && !form.sparkId)
              }
              isLoading={isReporting}
              theme="cta"
              onClick={mutation}
            >
              {t('publication.confirmPublishing')}
            </Button>
          )}
        </div>
      </div>

      {dayjs(publication.date_publish).isAfter(dayjs()) && (
        <div className={styles.preventLayer}>
          <Clock />
          <div className={styles.preventTitle}>
            <Trans
              i18nKey="collaborations.publish.title"
              values={{
                count: dayjs(publication.date_publish).diff(dayjs(), 'day') + 1
              }}
              components={{
                1: <span />
              }}
            />
          </div>
          <div className={styles.preventDescription}>
            <Trans
              i18nKey="collaborations.publish.description"
              values={{
                date: dayjs(publication.date_publish).format('DD.MM.YYYY')
              }}
              components={{
                1: <span />
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
